import type { Router } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { useTeamStore } from '@/stores/teamStore'
import { useInfoStore } from '@/stores/info'
import { getUserHomePath } from '@/composables/getUserHomePath'
import { hasActiveTeam, hasOrganization, isDeveloper, isOrgLeader, isScrumMaster, isTeamLeader, isTPAAdmin } from '@/composables/user'
import { usePermissions } from '@/composables/usePermissions'
import { useRouteLoading } from '@/composables/useRouteLoading'
import { tGlobal } from '../../i18n'

// Guards run outside any component (no `useI18n()` scope), so they translate
// through the global instance at call time and the toast follows the active locale.
const t = tGlobal

export async function appRouteGuard(to: any, from: any, next: any) {
  console.log('Navigating from', from.fullPath, 'to', to.fullPath)

  // Check if route is public
  if (!to.meta.requiresAuth) {
    return next()
  }

  const userStore = useUserStore()
  await userStore.loadCurrentUserRBAC()
  const teamStore = useTeamStore()
  const infoStore = useInfoStore()
  const { hasPermission, hasModule, hasView } = usePermissions()

  // Ensure current user data is loaded
  const userHomePath = getUserHomePath(userStore.currentUser)

  // Check if user is authenticated. If /me failed (expired or revoked
  // token), the 401 interceptor has already cleared storage and pushed
  // /signin. Force the redirect here too without any toast - the user
  // must never see a protected view in this state.
  const isAuthenticated = !!userStore.currentUser
  if (!isAuthenticated) {
    return next({ name: 'signin' })
  }

  // New dynamic checks
  if (to.meta.requiresPermission) {
    const { entity, action } = to.meta.requiresPermission as { entity: string, action: any }
    if (!hasPermission(entity, action)) {
      infoStore.trigger(t('common.access.permission'), 'warning')
      return next(userHomePath)
    }
  }

  if (to.meta.requiresModule) {
    if (!hasModule(to.meta.requiresModule as string)) {
      // Find the first accessible module route
      const userModules = (userStore.currentUser?.roles || [])
        .flatMap((role: any) => role.modules || [])
        .map((mod: any) => mod.name)
      // List of your module route names in order of preference
      const moduleRouteNames = [
        'dashboard',
        'realizar-test',
        // add more as needed
      ]
      const firstAccessible = moduleRouteNames.find(name => userModules.includes(name))
      if (firstAccessible) {
        infoStore.trigger(t('common.access.moduleRedirect'), 'warning')
        return next({ name: firstAccessible })
      }
      infoStore.trigger(t('common.access.module'), 'warning')
      return next(userHomePath)
    }
  }

  if (to.meta.requiresView) {
    if (!hasView(to.meta.requiresView as string)) {
      infoStore.trigger(t('common.access.view'), 'warning')
      return next(userHomePath)
    }
  }

  // tpa_admin-only routes (e.g. the /app approval dashboard, the
  // smartway-admin section, and organization creation). The flag was declared
  // on some routes but never enforced, so any authenticated user could reach
  // them by URL.
  if (to.meta.requiresAdminTPA) {
    if (!isTPAAdmin(userStore.currentUser)) {
      infoStore.trigger(t('common.access.page'), 'warning')
      return next(userHomePath)
    }
  }

  // Organization list/detail: tpa_admin OR org_leader. Org leaders may browse
  // the organizations list and open an organization's detail view, but not
  // create organizations (that stays tpa_admin-only via requiresAdminTPA).
  if (to.meta.requiresAdminTPAorOrgLeader) {
    const user = userStore.currentUser
    if (!isTPAAdmin(user) && !isOrgLeader(user)) {
      infoStore.trigger(t('common.access.page'), 'warning')
      return next(userHomePath)
    }
  }

  // Check if route requires not having an organization
  if (to.meta.requiresAdminTPAOrNoOrg) {
    if (hasOrganization(userStore.currentUser) && !isTPAAdmin(userStore.currentUser)) {
      infoStore.trigger(t('common.access.alreadyInOrganization'), 'warning')
      return next(userHomePath)
    }
  }

  // Check if route requires an organization. tpa_admins bypass, mirroring
  // requiresAdminTPAOrNoOrg and requiresOrgActivation below: they administer
  // every organization and belong to the always-on Smartway org, so an org
  // gate must never bounce them. Without this, a tpa_admin whose SmartWay
  // user_entities link is missing is locked out of the whole app with no way
  // back - the guard is the last line, not the place to detect bad data.
  if (to.meta.requiresOrganization) {
    if (!hasOrganization(userStore.currentUser) && !isTPAAdmin(userStore.currentUser)) {
      infoStore.trigger(t('common.access.organizationRequired'), 'warning')
      return next(userHomePath)
    }
  }

  // Check if route requires the user's organization to be activated. The
  // flag was declared on the approval dashboard plus the whole teams/users
  // sections but never enforced, so members of a not-yet-activated
  // organization could still reach these views by URL. tpa_admins bypass -
  // they manage activation and belong to the always-on Smartway org.
  //
  // Denials redirect to `help` (an in-app page with no org/team gating)
  // rather than `userHomePath`: a team member's home is itself an
  // org-activation-gated teams route, so redirecting there would loop.
  if (to.meta.requiresOrgActivation) {
    const user = userStore.currentUser
    if (!isTPAAdmin(user) && !user?.organization?.activated) {
      infoStore.trigger(t('common.access.organizationNotActivated'), 'warning')
      return next({ name: 'help' })
    }
  }

  // Only run Team Leader/Admin check if authenticated
  if (isAuthenticated && to.meta.requiresAdminTPAorTL) {
    const user = userStore.currentUser

    // Collect every team id where this user holds team_leader, drawing from
    // both RBAC role context (`role.context.teams`) and legacy `teamUsers`.
    // The union is what "can this user access TL views" means - a user who
    // leads *any* team should never be bounced from a TL route.
    const leaderTeamIds = new Set<string>()
    for (const role of (user?.roles || [])) {
      if ((role as any).name !== 'team_leader') continue
      const teams = (role as any).context?.teams
      if (Array.isArray(teams)) {
        for (const id of teams) leaderTeamIds.add(String(id))
      }
    }
    for (const tu of (user?.teamUsers || [])) {
      if ((tu as any).role === 'team_leader' && (tu as any).teamId) {
        leaderTeamIds.add(String((tu as any).teamId))
      }
    }

    // Developers bypass alongside tpa_admins: the team-scoped views are where
    // the developer-only tooling lives (the advice view hosts the Team Power
    // Dynamics section), and a developer holds no team membership of their own.
    if (!isTPAAdmin(user) && !isDeveloper(user) && leaderTeamIds.size === 0) {
      infoStore.trigger(t('common.access.teamLeaderRequired'), 'warning')
      return next(userHomePath)
    }

    // If the currently-selected team is not one this user leads, auto-switch
    // to a team they do lead. Keeps `teamStore.currentTeam` in sync with
    // permission context so the TL views render for a team the user can
    // actually act on. No toast - this is a silent correction, not a denial.
    const currentTeamId = teamStore.currentTeam?.id
    if (
      !isTPAAdmin(user) &&
      !isDeveloper(user) &&
      (!currentTeamId || !leaderTeamIds.has(String(currentTeamId)))
    ) {
      const switchToId = [...leaderTeamIds][0]
      const switchTo = teamStore.teams?.[switchToId]
      if (switchTo) {
        await teamStore.setCurrentTeam(switchTo)
      }
    }
  }

  // Results view: tpa_admin OR any team leader OR any scrum master OR any
  // org leader (viewing one of their organization's teams, e.g. via the
  // team profile's "Results" tab). Access is granted by ROLE PRESENCE - the
  // backend scopes the actual results per session, and a scrum master's
  // specific team context is not always present on the user object, so
  // requiring a resolved team id here would wrongly bounce a legitimate
  // scrum master.
  if (isAuthenticated && to.meta.requiresAdminTPAorTLorSM) {
    const user = userStore.currentUser

    if (
      !isTPAAdmin(user) &&
      !isDeveloper(user) &&
      !isTeamLeader(user) &&
      !isScrumMaster(user) &&
      !isOrgLeader(user)
    ) {
      infoStore.trigger(t('common.access.teamResults'), 'warning')
      return next(userHomePath)
    }

    // Best-effort: keep the selected team in sync with a team this user
    // actually belongs to (leader or scrum master), when one can be resolved.
    const accessTeamIds = new Set<string>()
    for (const role of (user?.roles || [])) {
      const name = (role as any).name
      if (name !== 'team_leader' && name !== 'scrum_master') continue
      const teams = (role as any).context?.teams
      if (Array.isArray(teams)) {
        for (const id of teams) accessTeamIds.add(String(id))
      }
    }
    for (const tu of (user?.teamUsers || [])) {
      const role = (tu as any).role
      if ((role === 'team_leader' || role === 'scrum_master') && (tu as any).teamId) {
        accessTeamIds.add(String((tu as any).teamId))
      }
    }
    const currentTeamId = teamStore.currentTeam?.id
    if (
      !isTPAAdmin(user) &&
      accessTeamIds.size > 0 &&
      (!currentTeamId || !accessTeamIds.has(String(currentTeamId)))
    ) {
      const switchToId = [...accessTeamIds][0]
      const switchTo = teamStore.teams?.[switchToId]
      if (switchTo) {
        await teamStore.setCurrentTeam(switchTo)
      }
    }
  }

  if (to.meta.requiresTeamActivation) {
    // Same rationale as requiresAdminTPAorTL above - a developer has no team of
    // their own, so an activation check sourced from their memberships can only
    // ever fail and would lock them out of every team-scoped view.
    if (!isDeveloper(userStore.currentUser) && !hasActiveTeam(userStore.currentUser)) {
      await userStore.loadCurrentUser()
      if (!hasActiveTeam(userStore.currentUser)) {
        infoStore.trigger(t('common.access.teamNotActivated'), 'warning')
        return next(userHomePath)
      }
    }
  }

  return next()
}

// Public routes that should never carry over a stale popup from a previous
// (protected) route. Arriving here means the user's session is effectively
// reset - any warning about the session they *just* had is noise.
const POPUP_RESET_ROUTES = new Set(['signin', 'connect', 'home'])

export function registerRouteGuards(router: Router) {
  const { startLoading, stopLoading } = useRouteLoading()

  router.beforeEach((to, from, next) => {
    if (POPUP_RESET_ROUTES.has(String(to.name ?? ''))) {
      useInfoStore().clear()
    }
    startLoading(String(to.name ?? ''))
    appRouteGuard(to, from, next)
  })

  router.afterEach(() => {
    stopLoading()
  })
}
